import { Box, Button, HStack, Icon, Image, Text, VStack, useToast } from '@chakra-ui/react';
import { IoCopyOutline, IoPrintOutline } from 'react-icons/io5';
import bwipjs from 'bwip-js';
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import bancolombiaLogo from '../assets/bancolombia.png';

type Props = {
  agreement: string;
  reference: string;
  barcode: string;
  amount: number;
  customerName?: string;
  customerDocumentNumber?: string;
  description?: string;
  dueDate?: string;
};

type PrintReceiptProps = {
  copyLabel: string;
  agreement: string;
  reference: string;
  amount: string;
  customerName: string;
  customerDocumentNumber: string;
  description: string;
  dueDate: string;
  generatedAt: string;
  canvasRef: RefObject<HTMLCanvasElement>;
};

const formatDate = (value?: string) => {
  if (!value) return '-';

  return new Date(value).toLocaleDateString('es-CO', {
    timeZone: 'UTC',
  });
};

const ScreenRow = ({ label, value, onCopy }: { label: string; value: string; onCopy?: () => void }) => (
  <HStack justify="space-between" spacing={3} align="flex-start" py={1.5} borderBottom="1px solid" borderColor="gray.100">
    <Text color="gray.500" fontSize="sm" flexShrink={0}>
      {label}
    </Text>
    <HStack spacing={2} justify="flex-end" minW={0}>
      <Text fontWeight="700" fontSize="sm" color="gray.900" textAlign="right" wordBreak="break-word">
        {value || '-'}
      </Text>
      {onCopy && <Icon as={IoCopyOutline} boxSize={4} color="gray.500" cursor="pointer" onClick={onCopy} />}
    </HStack>
  </HStack>
);

const PrintRow = ({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) => (
  <div className={strong ? 'cash-print-row cash-print-row-strong' : 'cash-print-row'}>
    <span>{label}</span>
    <strong>{value || '-'}</strong>
  </div>
);

const PrintReceipt = ({
  copyLabel,
  agreement,
  reference,
  amount,
  customerName,
  customerDocumentNumber,
  description,
  generatedAt,
  canvasRef,
}: PrintReceiptProps) => (
  <section className="cash-print-copy">
    <header className="cash-print-card-header">
      <div>
        <h1>Pago en efectivo Bancolombia</h1>
        <p>Presenta este comprobante en una sucursal o corresponsal.</p>
      </div>
      <div className="cash-print-header-side">
        <img src={bancolombiaLogo} alt="Bancolombia" />
        <strong>{copyLabel}</strong>
      </div>
    </header>

    <div className="cash-print-panel">
      <PrintRow label="Comercio" value="Palomma S.A.S. - NIT 901.653.730" />
      <PrintRow label="Referencia" value={reference} />
      <PrintRow label="Convenio" value={agreement} />
      <PrintRow label="Pagador" value={customerName} />
      <PrintRow label="Documento" value={customerDocumentNumber} />
      <PrintRow label="Concepto" value={description} />
      <PrintRow label="Total a pagar" value={amount} strong />
    </div>

    <div className="cash-print-barcode-card">
      <canvas ref={canvasRef} />
    </div>

    <footer className="cash-print-card-footer">
      <span>
        Este recibo es válido únicamente si tiene el timbre de la entidad financiera o el soporte electrónico de pago.
      </span>
      <div>
        <span>Generado el: {generatedAt}</span>
      </div>
    </footer>
  </section>
);

export const BancolombiaCashPaymentAwait = ({
  agreement,
  reference,
  barcode,
  amount,
  customerName,
  customerDocumentNumber,
  description,
  dueDate,
}: Props) => {
  const toast = useToast();
  const formatCurrency = useCurrencyFormatter();
  const screenCanvasRef = useRef<HTMLCanvasElement>(null);
  const payerPrintCanvasRef = useRef<HTMLCanvasElement>(null);
  const bankPrintCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const barcodeTargets = [
      { canvas: screenCanvasRef.current, height: 6, scale: 2, includeText: false },
      { canvas: payerPrintCanvasRef.current, height: 9, scale: 3, includeText: true },
      { canvas: bankPrintCanvasRef.current, height: 9, scale: 3, includeText: true },
    ];

    barcodeTargets.forEach(({ canvas, height, scale, includeText }) => {
      if (!canvas) return;

      try {
        bwipjs.toCanvas(canvas, {
          bcid: 'gs1-128',
          text: barcode,
          scale,
          height,
          includetext: includeText,
          textxalign: 'center',
          textsize: 10,
          paddingwidth: 4,
          paddingheight: 1,
        });
      } catch (e) {
        console.error('Error generating barcode:', e);
      }
    });
  }, [barcode]);

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    toast({
      title: `${label} copiado al portapapeles`,
      status: 'success',
      duration: 2000,
    });
  };

  const formattedAmount = formatCurrency(amount);
  const formattedDueDate = formatDate(dueDate);
  const payerName = customerName || customerDocumentNumber || 'Pago en corresponsal';
  const documentNumber = customerDocumentNumber || '-';
  const paymentDescription = description || 'Pago de factura';
  const generatedAt = new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'short',
    timeStyle: 'medium',
    hour12: false,
    timeZone: 'America/Bogota',
  }).format(new Date());

  return (
    <>
      <style>{`
        .cash-print-root { display: none; }

        @page {
          size: A4 portrait;
          margin: 6mm;
        }

        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }

          body * { visibility: hidden; }

          #bancolombia-cash-print,
          #bancolombia-cash-print * {
            visibility: visible;
          }

          #bancolombia-cash-print {
            position: fixed;
            inset: 0;
            margin: 0 !important;
            padding: 0 !important;
            background: white;
            box-sizing: border-box;
          }

          .cash-screen-root,
          .no-print {
            display: none !important;
          }

          .cash-print-root {
            display: flex !important;
            flex-direction: column;
            gap: 8mm;
            width: 100%;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
            color: #171923;
          }

          .cash-print-copy {
            border: 1px dashed #cbd5e0;
            border-radius: 14px;
            padding: 7mm;
            display: flex;
            flex-direction: column;
            gap: 4mm;
            break-inside: avoid;
            page-break-inside: avoid;
            box-sizing: border-box;
          }

          .cash-print-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8mm;
          }

          .cash-print-card-header h1,
          .cash-print-card-header p {
            margin: 0;
          }

          .cash-print-card-header h1 {
            font-size: 18px;
            line-height: 1.15;
            font-weight: 800;
          }

          .cash-print-card-header p {
            color: #718096;
            font-size: 12px;
            line-height: 1.25;
            margin-top: 1.5mm;
          }

          .cash-print-header-side {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1.5mm;
            flex-shrink: 0;
          }

          .cash-print-header-side img {
            width: 13mm;
            height: 13mm;
            border-radius: 999px;
            background: #f7fafc;
            padding: 2.5mm;
            box-sizing: border-box;
          }

          .cash-print-header-side strong {
            font-size: 10px;
            line-height: 1;
            color: #4a5568;
          }

          .cash-print-panel {
            background: #f7fafc;
            border-radius: 12px;
            padding: 2mm 4mm;
          }

          .cash-print-row {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
            gap: 8mm;
            border-bottom: 1px solid #e2e8f0;
            padding: 1.6mm 0;
          }

          .cash-print-row:last-child {
            border-bottom: 0;
          }

          .cash-print-row span {
            font-size: 11px;
            color: #4a5568;
            line-height: 1.2;
          }

          .cash-print-row strong {
            font-size: 11px;
            line-height: 1.2;
            text-align: right;
            overflow-wrap: anywhere;
          }

          .cash-print-row-strong strong {
            font-size: 14px;
          }

          .cash-print-barcode-card {
            border: 1px solid #e2e8f0;
            border-radius: 10px;
            padding: 3mm 4mm;
            text-align: center;
          }

          .cash-print-barcode-card canvas {
            display: block;
            width: 96%;
            max-width: 180mm;
            height: auto;
            margin: 0 auto;
          }

          .cash-print-card-footer {
            color: #4a5568;
            font-size: 9px;
            line-height: 1.25;
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            align-items: flex-start;
            border-top: 1px solid #e2e8f0;
            padding-top: 2mm;
          }

          .cash-print-card-footer > span {
            max-width: 120mm;
          }

          .cash-print-card-footer div {
            text-align: right;
            white-space: nowrap;
          }
        }
      `}</style>

      <Box mt={3} width="100%" id="bancolombia-cash-print">
        <VStack spacing={4} align="stretch" className="cash-screen-root">
          <Box border="1px dashed" borderColor="gray.300" borderRadius="xl" p={{ base: 3, md: 4 }} bg="white">
            <VStack spacing={3} align="stretch">
              <HStack justify="space-between" align="center" spacing={4}>
                <Box>
                  <Text fontWeight="800" fontSize={{ base: 'md', md: 'lg' }} color="gray.900">
                    Pago en efectivo Bancolombia
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Presenta este comprobante en una sucursal o corresponsal.
                  </Text>
                </Box>
                <Image src={bancolombiaLogo} alt="Bancolombia" boxSize="42px" borderRadius="full" bg="gray.50" p={2} />
              </HStack>

              <Box bg="gray.50" borderRadius="lg" px={3} py={2}>
                <ScreenRow label="Referencia" value={reference} onCopy={() => handleCopy(reference, 'Referencia')} />
                <ScreenRow label="Convenio" value={agreement} onCopy={() => handleCopy(agreement, 'Convenio')} />
                <ScreenRow label="Pagador" value={payerName} />
                <ScreenRow label="Total a pagar" value={formattedAmount} />
              </Box>

              <Box border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={2} textAlign="center" overflowX="auto">
                <Box minW="360px">
                  <canvas ref={screenCanvasRef} style={{ width: '100%', maxWidth: '520px' }} />
                </Box>
                <Text mt={1} fontSize="0.65rem" color="gray.500" fontFamily="mono" wordBreak="break-all">
                  {barcode}
                </Text>
              </Box>
            </VStack>
          </Box>

          <Text fontSize="sm" textAlign="center" color="gray.600" px={4} className="no-print">
            Una vez realizado el pago, el sistema se actualizará automáticamente. Conserva tu comprobante sellado.
          </Text>

          <Text fontSize="xs" textAlign="center" color="gray.500" px={4} className="no-print">
            Bancolombia no acepta pagos con Cheques Remesas.
          </Text>

          <Button
            className="no-print"
            leftIcon={<Icon as={IoPrintOutline} />}
            variant="outline"
            width="100%"
            onClick={() => window.print()}
          >
            Imprimir comprobante
          </Button>
        </VStack>

        <div className="cash-print-root">
          <PrintReceipt
            copyLabel="PAGADOR"
            agreement={agreement}
            reference={reference}
            amount={formattedAmount}
            customerName={payerName}
            customerDocumentNumber={documentNumber}
            description={paymentDescription}
            dueDate={formattedDueDate}
            generatedAt={generatedAt}
            canvasRef={payerPrintCanvasRef}
          />
          <PrintReceipt
            copyLabel="BANCO"
            agreement={agreement}
            reference={reference}
            amount={formattedAmount}
            customerName={payerName}
            customerDocumentNumber={documentNumber}
            description={paymentDescription}
            dueDate={formattedDueDate}
            generatedAt={generatedAt}
            canvasRef={bankPrintCanvasRef}
          />
        </div>
      </Box>
    </>
  );
};
